import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../components/inputs/FractionInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const stripUnits = (raw) => String(raw || "").replace(/[^\d.,/-]+/g, "");
const parseAngle = (raw) => parseFraction(stripUnits(raw));
const degreeLatex = (value) => `${value}^{\\circ}`;

const TYPES = {
  TriangleAngleSumTask: "sum",
  TriangleExteriorAdjacentAngleTask: "adjacent",
  TriangleVerticalAngleTask: "vertical",
  IsoscelesTriangleAnglesTask: "isosceles",
};

const TYPE_ORDER = Object.keys(TYPES);
const TAGS = ["planimetria", "trojkaty", "katy", "suma-katow"];

const variants = {
  sum: [
    { a: 48, b: 67 },
    { a: 35, b: 85 },
    { a: 42, b: 78 },
    { a: 56, b: 49 },
    { a: 72, b: 38 },
  ],
  adjacent: [
    { a: 54, exterior: 118 },
    { a: 47, exterior: 119 },
    { a: 62, exterior: 104 },
    { a: 38, exterior: 132 },
    { a: 73, exterior: 96 },
  ],
  vertical: [
    { a: 52, vertical: 68 },
    { a: 45, vertical: 72 },
    { a: 64, vertical: 51 },
    { a: 39, vertical: 86 },
    { a: 58, vertical: 43 },
  ],
  isosceles: [
    { base: 48 },
    { base: 55 },
    { base: 63 },
    { base: 72 },
    { apex: 46, unknownBase: "left" },
    { apex: 82, unknownBase: "right" },
    { apex: 110, unknownBase: "left" },
    { apex: 64, unknownBase: "right" },
  ],
};

function angleAnswer(data) {
  if (data.mode === "sum") return 180 - data.a - data.b;
  if (data.mode === "adjacent") return 180 - data.a - (180 - data.exterior);
  if (data.mode === "vertical") return 180 - data.a - data.vertical;
  if (data.mode === "isosceles") return data.apex != null ? (180 - data.apex) / 2 : 180 - 2 * data.base;
  return 0;
}

function AngleInput({ userAnswer, onChange, disabled }) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  return (
    <FractionInput
      value={raw}
      onChange={(value) =>
        onChange({
          raw: value?.raw ?? "",
          parsed: parseAngle(value?.raw ?? ""),
        })
      }
      disabled={disabled}
      label="\\alpha"
      suffixLatex={"^\\circ"}
      placeholder="np. 65"
    />
  );
}

function SvgMathLabel({ x, y, math, color = "currentColor", width = 72, height = 28, anchor = "middle", size = 16 }) {
  const boxX = anchor === "start" ? x : anchor === "end" ? x - width : x - width / 2;
  const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";
  return (
    <foreignObject x={boxX} y={y - height / 2} width={width} height={height} style={{ overflow: "visible" }}>
      <div style={{ color, fontSize: size, lineHeight: 1.2, textAlign, whiteSpace: "nowrap" }}>
        <InlineMath math={math} />
      </div>
    </foreignObject>
  );
}


function rad(deg) {
  return (deg * Math.PI) / 180;
}


function intersectRays(a, angleA, b, angleB) {
  const r = { x: Math.cos(rad(angleA)), y: Math.sin(rad(angleA)) };
  const v = { x: Math.cos(rad(angleB)), y: Math.sin(rad(angleB)) };
  const den = r.x * v.y - r.y * v.x;
  if (Math.abs(den) < 0.001) return { x: 220, y: 58 };
  const t = ((b.x - a.x) * v.y - (b.y - a.y) * v.x) / den;
  return { x: a.x + t * r.x, y: a.y + t * r.y };
}

function triangleAngles(data) {
  if (data.mode === "sum") return { a: data.a, b: data.b, c: angleAnswer(data) };
  if (data.mode === "adjacent") {
    const b = 180 - data.exterior;
    return { a: data.a, b, c: angleAnswer(data) };
  }
  if (data.mode === "vertical") return { a: data.a, b: data.vertical, c: angleAnswer(data) };
  if (data.mode === "isosceles") {
    if (data.apex != null) {
      const base = (180 - data.apex) / 2;
      return { a: base, b: base, c: data.apex };
    }
    return { a: data.base, b: data.base, c: angleAnswer(data) };
  }
  return { a: 60, b: 60, c: 60 };
}

function trianglePoints(data) {
  const angles = triangleAngles(data);
  const rawA = { x: 0, y: 0 };
  const rawB = { x: 1, y: 0 };
  const rawC = intersectRays(rawA, -angles.a, rawB, 180 + angles.b);
  const xs = [rawA.x, rawB.x, rawC.x];
  const ys = [rawA.y, rawB.y, rawC.y];
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const scale = Math.min(300 / Math.max(maxX - minX, 0.1), 118 / Math.max(maxY - minY, 0.1));
  const xOffset = 200 - ((minX + maxX) * scale) / 2;
  const yOffset = 155;
  const map = (point) => ({ x: point.x * scale + xOffset, y: point.y * scale + yOffset });
  return { A: map(rawA), B: map(rawB), C: map(rawC), angles };
}

function unitVector(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

function extendPoint(from, through, length) {
  const u = unitVector(from, through);
  return { x: through.x + u.x * length, y: through.y + u.y * length };
}

function arcPath(center, p1, p2, radius) {
  const a1 = Math.atan2(p1.y - center.y, p1.x - center.x);
  let a2 = Math.atan2(p2.y - center.y, p2.x - center.x);
  let diff = a2 - a1;
  while (diff <= -Math.PI) diff += Math.PI * 2;
  while (diff > Math.PI) diff -= Math.PI * 2;
  a2 = a1 + diff;
  const startPoint = { x: center.x + radius * Math.cos(a1), y: center.y + radius * Math.sin(a1) };
  const endPoint = { x: center.x + radius * Math.cos(a2), y: center.y + radius * Math.sin(a2) };
  return {
    d: "M " + startPoint.x + " " + startPoint.y + " A " + radius + " " + radius + " 0 0 " + (diff > 0 ? 1 : 0) + " " + endPoint.x + " " + endPoint.y,
    midAngle: a1 + diff / 2,
  };
}

function labelOnArc(center, p1, p2, radius, offset = 17) {
  const arc = arcPath(center, p1, p2, radius);
  return {
    x: center.x + (radius + offset) * Math.cos(arc.midAngle),
    y: center.y + (radius + offset) * Math.sin(arc.midAngle),
  };
}

function sideLabel(a, b, offsetX = 0, offsetY = 0) {
  return { x: (a.x + b.x) / 2 + offsetX, y: (a.y + b.y) / 2 + offsetY };
}

function AngleArc({ center, p1, p2, radius = 24, color }) {
  const arc = arcPath(center, p1, p2, radius);
  return <path d={arc.d} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />;
}

function TriangleAnglesDiagram({ data, reveal = false }) {
  const colors = {
    accent: "var(--color-accent, #f59e0b)",
    unknown: "#a855f7",
    base: "currentColor",
    fill: "rgba(245, 158, 11, 0.14)",
  };
  const { A, B, C } = trianglePoints(data);
  const alpha = reveal ? degreeLatex(angleAnswer(data)) : "\\alpha";
  const rightExtension = { x: B.x + 50, y: B.y };
  const leftExtension = { x: A.x - 26, y: A.y };
  const bToCExtension = extendPoint(C, B, 44);
  const labelA = labelOnArc(A, B, C, 23, 19);
  const labelB = labelOnArc(B, C, A, 23, 13);
  const adjacentInterior = data.mode === "adjacent" ? 180 - data.exterior : null;
  const labelC = labelOnArc(C, A, B, 24, 16);
  const labelExteriorB = labelOnArc(B, C, rightExtension, 29, 15);
  const labelVerticalB = labelOnArc(B, rightExtension, bToCExtension, 27, 17);
  const labelSideAC = sideLabel(A, C, -19, -4);
  const labelSideBC = sideLabel(B, C, 19, -4);

  const showA = data.mode !== "isosceles" || data.base != null;
  const showB = data.mode === "sum" || (reveal && (data.mode === "adjacent" || data.mode === "vertical"));
  const showC = data.mode === "sum" || data.mode === "adjacent" || data.mode === "vertical" || data.mode === "isosceles";
  const aLabel = degreeLatex(data.a ?? data.base);
  const bLabel = data.mode === "adjacent" ? degreeLatex(adjacentInterior) : degreeLatex(data.vertical ?? data.b);
  const isUnknownBaseAngle = data.mode === "isosceles" && data.apex != null;
  const cLabel = isUnknownBaseAngle ? degreeLatex(data.apex) : alpha;
  const unknownColor = colors.unknown;
  const cAngleColor = isUnknownBaseAngle ? colors.accent : unknownColor;
  const baseUnknownPoint = data.unknownBase === "right" ? B : A;
  const baseUnknownLabel = data.unknownBase === "right" ? labelB : labelA;
  const points = A.x + "," + A.y + " " + B.x + "," + B.y + " " + C.x + "," + C.y;

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "8px 0 10px" }}>
      <svg width="100%" height="215" viewBox="0 0 400 230" role="img" aria-label="Trójkąt z oznaczonymi kątami" style={{ maxWidth: 470 }}>
        {(data.mode === "adjacent" || data.mode === "vertical") && (
          <line x1={leftExtension.x} y1={leftExtension.y} x2={rightExtension.x} y2={rightExtension.y} stroke={colors.base} strokeWidth="2.8" opacity="0.9" strokeLinecap="round" />
        )}

        {data.mode === "vertical" && (
          <>
            <line x1={B.x} y1={B.y} x2={bToCExtension.x} y2={bToCExtension.y} stroke={colors.base} strokeWidth="2.8" opacity="0.78" strokeLinecap="round" />
            <AngleArc center={B} p1={rightExtension} p2={bToCExtension} radius={27} color={colors.accent} />
            <SvgMathLabel x={labelVerticalB.x} y={labelVerticalB.y} math={degreeLatex(data.vertical)} color={colors.accent} width={66} size={15} />
          </>
        )}

        <polygon points={points} fill={colors.fill} stroke={colors.base} strokeWidth="3" strokeLinejoin="round" />

        <SvgMathLabel x={A.x - 20} y={A.y + 20} math="A" size={15} />
        <SvgMathLabel x={data.mode === "vertical" ? B.x - 22 : B.x + 20} y={B.y + 20} math="B" size={15} />
        <SvgMathLabel x={C.x} y={C.y - 17} math="C" size={15} />

        {showA && (
          <>
            <AngleArc center={A} p1={B} p2={C} radius={23} color={colors.accent} />
            <SvgMathLabel x={labelA.x} y={labelA.y} math={aLabel} color={colors.accent} width={62} size={15} />
          </>
        )}

        {showB && (
          <>
            <AngleArc center={B} p1={C} p2={A} radius={23} color={colors.accent} />
            <SvgMathLabel x={labelB.x} y={labelB.y} math={bLabel} color={colors.accent} width={62} size={15} />
          </>
        )}

        {showC && (
          <>
            <AngleArc center={C} p1={A} p2={B} radius={24} color={cAngleColor} />
            <SvgMathLabel x={labelC.x} y={labelC.y} math={cLabel} color={cAngleColor} width={66} size={15} />
          </>
        )}

        {data.mode === "adjacent" && (
          <>
            <AngleArc center={B} p1={C} p2={rightExtension} radius={29} color={colors.accent} />
            <SvgMathLabel x={labelExteriorB.x} y={labelExteriorB.y} math={degreeLatex(data.exterior)} color={colors.accent} width={66} size={15} />
          </>
        )}

        {data.mode === "isosceles" && (
          <>
            <SvgMathLabel x={labelSideAC.x} y={labelSideAC.y} math="a" color={colors.base} width={34} size={15} />
            <SvgMathLabel x={labelSideBC.x} y={labelSideBC.y} math="a" color={colors.base} width={34} size={15} />
            {isUnknownBaseAngle && (
              <>
                <AngleArc center={baseUnknownPoint} p1={baseUnknownPoint === A ? B : C} p2={baseUnknownPoint === A ? C : A} radius={23} color={unknownColor} />
                <SvgMathLabel x={baseUnknownLabel.x} y={baseUnknownLabel.y} math={alpha} color={unknownColor} width={62} size={15} />
              </>
            )}
          </>
        )}
      </svg>
    </div>
  );
}

function statement(data) {
  if (data.mode === "sum") {
    return <>W trójkącie <InlineMath math="ABC" /> dane są kąty <InlineMath math={`\\angle A=${degreeLatex(data.a)}`} /> oraz <InlineMath math={`\\angle B=${degreeLatex(data.b)}`} />. Oblicz miarę kąta oznaczonego <InlineMath math={"\\alpha"} />.</>;
  }
  if (data.mode === "adjacent") {
    return <>W trójkącie <InlineMath math="ABC" /> kąt przy wierzchołku <InlineMath math="A" /> ma miarę <InlineMath math={degreeLatex(data.a)} />. Przy wierzchołku <InlineMath math="B" /> podano kąt zewnętrzny o mierze <InlineMath math={degreeLatex(data.exterior)} />. Oblicz miarę kąta oznaczonego <InlineMath math={"\\alpha"} />.</>;
  }
  if (data.mode === "vertical") {
    return <>Przedłużono boki trójkąta przy wierzchołku <InlineMath math="B" />. Jeden z kątów zewnętrznych przy tym wierzchołku ma miarę <InlineMath math={degreeLatex(data.vertical)} />, a <InlineMath math={`\\angle A=${degreeLatex(data.a)}`} />. Oblicz miarę kąta oznaczonego <InlineMath math={"\\alpha"} />.</>;
  }
  if (data.mode === "isosceles") {
    if (data.apex != null) {
      return <>Trójkąt <InlineMath math="ABC" /> jest równoramienny i <InlineMath math="AC=BC" />. Kąt między ramionami ma miarę <InlineMath math={degreeLatex(data.apex)} />. Oblicz miarę kąta oznaczonego <InlineMath math={"\\alpha"} />.</>;
    }
    return <>Trójkąt <InlineMath math="ABC" /> jest równoramienny i <InlineMath math="AC=BC" />. Kąt przy podstawie ma miarę <InlineMath math={degreeLatex(data.base)} />. Oblicz miarę kąta oznaczonego <InlineMath math={"\\alpha"} />.</>;
  }
  return null;
}
function explanation(data) {
  const x = angleAnswer(data);
  if (data.mode === "sum") {
    return [
      { text: "Korzystamy z sumy kątów w trójkącie.", math: `\\alpha=180^\\circ-${data.a}^\\circ-${data.b}^\\circ=${x}^\\circ` },
    ];
  }
  if (data.mode === "adjacent") {
    const inside = 180 - data.exterior;
    return [
      { text: "Kąt wewnętrzny przy B i kąt przyległy tworzą kąt półpełny.", math: `\\angle B=180^\\circ-${data.exterior}^\\circ=${inside}^\\circ` },
      { text: "Teraz korzystamy z sumy kątów w trójkącie.", math: `\\alpha=180^\\circ-${data.a}^\\circ-${inside}^\\circ=${x}^\\circ` },
    ];
  }
  if (data.mode === "vertical") {
    return [
      { text: "Kąt wierzchołkowy ma taką samą miarę jak kąt przy wierzchołku B.", math: `\\angle B=${data.vertical}^\\circ` },
      { text: "Doliczamy brakujący kąt przy wierzchołku C z sumy kątów trójkąta.", math: `\\alpha=180^\\circ-${data.a}^\\circ-${data.vertical}^\\circ=${x}^\\circ` },
    ];
  }
  if (data.mode === "isosceles") {
    if (data.apex != null) {
      return [
        { text: "W trójkącie równoramiennym kąty przy podstawie są równe.", math: `\\alpha=\\frac{180^\\circ-${data.apex}^\\circ}{2}=${x}^\\circ` },
      ];
    }
    return [
      { text: "Kąty przy podstawie są równe, więc odejmujemy dwa takie same kąty.", math: `\\alpha=180^\\circ-2\\cdot ${data.base}^\\circ=${x}^\\circ` },
    ];
  }
  return [
    { text: "Przy prostych równoległych kąty odpowiadające mają równe miary.", math: `\\angle A=${data.line}^\\circ` },
    { text: "Następnie korzystamy z sumy kątów w trójkącie.", math: `\\alpha=180^\\circ-${data.line}^\\circ-${data.other}^\\circ=${x}^\\circ` },
  ];
}

export function generateTask(type) {
  const taskType = type && TYPES[type] ? type : pick(TYPE_ORDER);
  const mode = TYPES[taskType];
  const base = pick(variants[mode]);
  return {
    type: taskType,
    data: { ...base, mode },
    tags: TAGS,
  };
}

export function generateTasks() {
  return TYPE_ORDER.map((type) => generateTask(type));
}

export function renderTask(task) {
  const data = task.data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <p>{statement(data)}</p>
      <TriangleAnglesDiagram data={data} />
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return <AngleInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} />;
}

export function checkAnswer(task, userAnswer) {
  const parsed = typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
    ? userAnswer.parsed
    : parseAngle(typeof userAnswer === "string" ? userAnswer : userAnswer?.raw);
  return Number.isFinite(parsed) && Math.abs(parsed - angleAnswer(task.data)) < 0.01;
}

export function renderAnswer(task) {
  return <InlineMath math={`\\alpha=${angleAnswer(task.data)}^\\circ`} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${formatLatexPreview(raw, "\\alpha")}^\\circ`} />;
}

export function renderExplanation(task) {
  const data = task.data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.8 }}>
      <TriangleAnglesDiagram data={data} reveal />
      {explanation(data).map((step, index) => (
        <React.Fragment key={`${step.math}-${index}`}>
          <p><strong>Krok {index + 1}.</strong> {step.text}</p>
          <BlockMath math={step.math} />
        </React.Fragment>
      ))}
      <p><strong>Odpowiedź.</strong> <InlineMath math={`x=${angleAnswer(data)}^\\circ`} />.</p>
    </div>
  );
}

export const katyWTrojkatachRegistry = Object.fromEntries(
  TYPE_ORDER.map((type) => [
    type,
    {
      renderTask: (task) => renderTask({ ...task, type }),
      renderInput: (task, userAnswer, onChange, disabled) => renderInput({ ...task, type }, userAnswer, onChange, disabled),
      checkAnswer: (task, userAnswer) => checkAnswer({ ...task, type }, userAnswer),
      renderAnswer: (task) => renderAnswer({ ...task, type }),
      renderExplanation: (task) => renderExplanation({ ...task, type }),
      renderUserAnswer: (userAnswer, task) => renderUserAnswer(userAnswer, { ...task, type }),
    },
  ])
);

export const registry = katyWTrojkatachRegistry;

const katyWTrojkatachModule = {
  generateTask,
  generateTasks,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
  registry,
};

export default katyWTrojkatachModule;